import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * PDF Service for generating professional reports
 * Supports Turkish language and modern layouts
 */
class PDFService {
  constructor() {
    this.doc = null;
    this.currentY = 20;
    this.pageWidth = 210; // A4 width in mm
    this.pageHeight = 297; // A4 height in mm
    this.margin = 20;
    this.colors = {
      primary: '#2c3e50',
      secondary: '#3498db',
      success: '#27ae60',
      warning: '#f39c12',
      danger: '#e74c3c',
      purple: '#8a2be2',
      gray: '#7f8c8d',
      lightGray: '#ecf0f1'
    };
  }

  /**
   * Initialize a new PDF document
   */
  initializePDF() {
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.currentY = 20;
    
    // Set up fonts for Turkish characters
    this.doc.setFont('helvetica');
    this.doc.setLanguage('tr-TR');
    
    return this.doc;
  }

  /**
   * Add header with logo and title
   */
  addHeader(title, subtitle, solverType) {
    // Company logo area (placeholder)
    this.doc.setFillColor(240, 240, 240);
    this.doc.rect(this.margin, 10, 40, 20, 'F');
    this.doc.setTextColor(100);
    this.doc.setFontSize(8);
    this.doc.text('RouteBox', this.margin + 2, 18);
    this.doc.text('Logo', this.margin + 2, 22);

    // Title and date
    this.doc.setTextColor(this.colors.primary);
    this.doc.setFontSize(18);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.margin + 50, 20);
    
    if (subtitle) {
      this.doc.setFontSize(12);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(subtitle, this.margin + 50, 28);
    }

    // Solver type badge
    if (solverType) {
      const badgeColor = this.getSolverColor(solverType);
      this.doc.setFillColor(badgeColor.r, badgeColor.g, badgeColor.b);
      this.doc.roundedRect(this.pageWidth - 50, 12, 30, 8, 2, 2, 'F');
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(solverType, this.pageWidth - 45, 17.5);
    }

    // Date
    this.doc.setTextColor(this.colors.gray);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    const date = new Date().toLocaleDateString('tr-TR');
    this.doc.text(`Tarih: ${date}`, this.pageWidth - 50, 25);

    // Horizontal line
    this.doc.setDrawColor(this.colors.lightGray);
    this.doc.line(this.margin, 35, this.pageWidth - this.margin, 35);

    this.currentY = 45;
  }

  /**
   * Get color for solver type
   */
  getSolverColor(solverType) {
    switch (solverType.toUpperCase()) {
      case 'VRP':
        return { r: 102, g: 126, b: 234 };
      case 'TSP':
        return { r: 240, g: 147, b: 251 };
      case 'LAP':
        return { r: 138, g: 43, b: 226 };
      default:
        return { r: 127, g: 140, b: 141 };
    }
  }

  /**
   * Add section title
   */
  addSectionTitle(title, color = this.colors.primary) {
    this.checkPageBreak(15);
    
    this.doc.setTextColor(color);
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.margin, this.currentY);
    
    // Underline
    this.doc.setDrawColor(color);
    this.doc.line(this.margin, this.currentY + 2, this.margin + 60, this.currentY + 2);
    
    this.currentY += 10;
  }

  /**
   * Add key-value pairs in a structured format
   */
  addKeyValueSection(data, columns = 2) {
    this.checkPageBreak(20);
    
    const startY = this.currentY;
    const columnWidth = (this.pageWidth - 2 * this.margin) / columns;
    let currentColumn = 0;
    let maxY = startY;

    Object.entries(data).forEach(([key, value], index) => {
      const x = this.margin + (currentColumn * columnWidth);
      const y = startY + Math.floor(index / columns) * 8;

      // Key
      this.doc.setTextColor(this.colors.gray);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(`${key}:`, x, y);

      // Value
      this.doc.setTextColor(this.colors.primary);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(String(value), x + 40, y);

      maxY = Math.max(maxY, y);
      currentColumn = (currentColumn + 1) % columns;
    });

    this.currentY = maxY + 10;
  }

  /**
   * Add table with data
   */
  addTable(headers, rows, columnWidths = null) {
    this.checkPageBreak(30);

    const tableWidth = this.pageWidth - 2 * this.margin;
    const defaultColumnWidth = tableWidth / headers.length;
    const colWidths = columnWidths || headers.map(() => defaultColumnWidth);

    // Table header
    this.doc.setFillColor(this.colors.primary);
    this.doc.rect(this.margin, this.currentY, tableWidth, 8, 'F');
    
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    
    let x = this.margin;
    headers.forEach((header, index) => {
      this.doc.text(header, x + 2, this.currentY + 5.5);
      x += colWidths[index];
    });

    this.currentY += 8;

    // Table rows
    this.doc.setTextColor(this.colors.primary);
    this.doc.setFont('helvetica', 'normal');
    
    rows.forEach((row, rowIndex) => {
      this.checkPageBreak(10);
      
      // Alternating row colors
      if (rowIndex % 2 === 0) {
        this.doc.setFillColor(248, 249, 250);
        this.doc.rect(this.margin, this.currentY, tableWidth, 7, 'F');
      }

      x = this.margin;
      row.forEach((cell, colIndex) => {
        this.doc.text(String(cell), x + 2, this.currentY + 5);
        x += colWidths[colIndex];
      });

      this.currentY += 7;
    });

    this.currentY += 5;
  }

  /**
   * Add metric cards in a grid layout
   */
  addMetricCards(metrics, cardsPerRow = 3) {
    this.checkPageBreak(25);

    const cardWidth = (this.pageWidth - 2 * this.margin - (cardsPerRow - 1) * 5) / cardsPerRow;
    const cardHeight = 20;
    let currentCard = 0;

    metrics.forEach((metric) => {
      const x = this.margin + (currentCard * (cardWidth + 5));
      const y = this.currentY;

      // Card background
      this.doc.setFillColor(248, 249, 250);
      this.doc.roundedRect(x, y, cardWidth, cardHeight, 2, 2, 'F');

      // Card border
      this.doc.setDrawColor(this.colors.lightGray);
      this.doc.roundedRect(x, y, cardWidth, cardHeight, 2, 2, 'S');

      // Metric value
      this.doc.setTextColor(metric.color || this.colors.primary);
      this.doc.setFontSize(16);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(metric.value, x + 5, y + 10);

      // Metric label
      this.doc.setTextColor(this.colors.gray);
      this.doc.setFontSize(9);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(metric.label, x + 5, y + 16);

      currentCard++;
      if (currentCard >= cardsPerRow) {
        currentCard = 0;
        this.currentY += cardHeight + 10;
      }
    });

    if (currentCard > 0) {
      this.currentY += cardHeight + 10;
    }
  }

  /**
   * Add progress bar
   */
  addProgressBar(label, percentage, color = this.colors.success) {
    this.checkPageBreak(12);

    // Label
    this.doc.setTextColor(this.colors.primary);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(label, this.margin, this.currentY);

    // Percentage
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(`${percentage}%`, this.pageWidth - this.margin - 15, this.currentY);

    // Progress bar background
    const barY = this.currentY + 2;
    const barWidth = this.pageWidth - 2 * this.margin - 20;
    this.doc.setFillColor(this.colors.lightGray);
    this.doc.roundedRect(this.margin, barY, barWidth, 4, 2, 2, 'F');

    // Progress bar fill
    const fillWidth = (barWidth * percentage) / 100;
    this.doc.setFillColor(color);
    this.doc.roundedRect(this.margin, barY, fillWidth, 4, 2, 2, 'F');

    this.currentY += 10;
  }

  /**
   * Add an image to the PDF
   */
  async addImage(imageData, width = null, height = null, title = null) {
    if (title) {
      this.addSectionTitle(title, this.colors.primary);
    }

    // Calculate optimal dimensions
    const maxWidth = this.pageWidth - 2 * this.margin;
    const maxHeight = 120; // Maximum height for images
    
    let imgWidth = width || maxWidth;
    let imgHeight = height || maxHeight;
    
    // Maintain aspect ratio if only one dimension is provided
    if (width && !height && imageData.width && imageData.height) {
      imgHeight = (imageData.height / imageData.width) * width;
    } else if (height && !width && imageData.width && imageData.height) {
      imgWidth = (imageData.width / imageData.height) * height;
    } else if (!width && !height && imageData.width && imageData.height) {
      // Auto-scale to fit within max dimensions
      const aspectRatio = imageData.width / imageData.height;
      if (aspectRatio > maxWidth / maxHeight) {
        imgWidth = maxWidth;
        imgHeight = maxWidth / aspectRatio;
      } else {
        imgHeight = maxHeight;
        imgWidth = maxHeight * aspectRatio;
      }
    }

    // Check if we need a page break
    this.checkPageBreak(imgHeight + 10);

    // Center the image horizontally
    const x = this.margin + (maxWidth - imgWidth) / 2;

    // Add the image
    this.doc.addImage(imageData, 'PNG', x, this.currentY, imgWidth, imgHeight);
    this.currentY += imgHeight + 10;
  }

  /**
   * Capture a DOM element as an image and add it to the PDF
   */
  async addElementAsImage(element, title = null, options = {}) {
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        ...options
      });
      
      const imageData = canvas.toDataURL('image/png');
      await this.addImage(imageData, null, null, title);
      
      return imageData;
    } catch (error) {
      console.error('Error capturing element as image:', error);
      // Add a placeholder if image capture fails
      this.addParagraph('Harita görüntüsü yüklenemedi. Lütfen tekrar deneyin.');
    }
  }

  /**
   * Create a map visualization for the route and add it to the PDF
   */
  async addMapVisualization(routeData, mapContainer, title = 'Rota Haritası') {
    if (!mapContainer) {
      this.addParagraph('Harita görüntüsü mevcut değil.');
      return;
    }

    try {
      // Wait for the map to fully load
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Capture the map container
      await this.addElementAsImage(mapContainer, title, {
        backgroundColor: '#ffffff',
        scale: 1.5,
        width: 800,
        height: 600,
        useCORS: true
      });
    } catch (error) {
      console.error('Error adding map visualization:', error);
      this.addParagraph('Harita görüntüsü oluşturulamadı.');
    }
  }

  /**
   * Add text paragraph with word wrapping
   */
  addParagraph(text, fontSize = 11) {
    this.checkPageBreak(15);

    this.doc.setTextColor(this.colors.primary);
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'normal');

    const maxWidth = this.pageWidth - 2 * this.margin;
    const lines = this.doc.splitTextToSize(text, maxWidth);

    lines.forEach(line => {
      this.checkPageBreak(5);
      this.doc.text(line, this.margin, this.currentY);
      this.currentY += 5;
    });

    this.currentY += 5;
  }

  /**
   * Check if we need a page break
   */
  checkPageBreak(requiredSpace) {
    if (this.currentY + requiredSpace > this.pageHeight - this.margin) {
      this.doc.addPage();
      this.currentY = this.margin;
    }
  }

  /**
   * Add footer with page numbers
   */
  addFooter() {
    const pageCount = this.doc.internal.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i);
      
      // Footer line
      this.doc.setDrawColor(this.colors.lightGray);
      this.doc.line(this.margin, this.pageHeight - 15, this.pageWidth - this.margin, this.pageHeight - 15);
      
      // Page number
      this.doc.setTextColor(this.colors.gray);
      this.doc.setFontSize(9);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        `Sayfa ${i} / ${pageCount}`, 
        this.pageWidth / 2, 
        this.pageHeight - 8, 
        { align: 'center' }
      );
      
      // Generated by text
      this.doc.text(
        'RouteBox Rota Optimizasyon Sistemi tarafından oluşturuldu',
        this.pageWidth / 2,
        this.pageHeight - 4,
        { align: 'center' }
      );
    }
  }

  /**
   * Format number for Turkish locale
   */
  formatNumber(number, decimals = 2) {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(number);
  }

  /**
   * Format date for Turkish locale
   */
  formatDate(date) {
    return new Date(date).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Save the PDF document
   */
  save(filename) {
    this.addFooter();
    this.doc.save(filename);
  }

  /**
   * Get the PDF as blob for further processing
   */
  getBlob() {
    this.addFooter();
    return this.doc.output('blob');
  }
}

export default new PDFService();